import type { UploadedFile, JobResponse } from "@taxassist/api-client";
import { fetchAuthSession } from "aws-amplify/auth";
import { appConfig } from "../config/environment";

/**
 * Extended UploadedFile type for future API enhancement
 * TODO: Remove this when submittedToSurePrep is added to the official API client types
 */
interface UploadedFileWithSubmissionStatus extends UploadedFile {
  submittedToSurePrep?: boolean;
}

/**
 * Error class for file deletion operations
 */
export class FileDeleteError extends Error {
  public readonly statusCode?: number;

  constructor(message: string, statusCode?: number) {
    super(message);
    this.name = "FileDeleteError";
    this.statusCode = statusCode;
  }
}

/**
 * Determines if a file can be deleted based on business rules
 */
export const canDeleteFile = (
  file: UploadedFile,
  job: JobResponse,
): { canDelete: boolean; reason?: string } => {
  // Check if job is in a state that allows file deletion
  if (job.status === "CANCELLED" || job.status === "ARCHIVED") {
    return {
      canDelete: false,
      reason: `Cannot delete files from ${job.status} job`,
    };
  }

  // Check if file has been submitted to SurePrep
  // TODO: API Enhancement - Use submittedToSurePrep field when available
  // For now, we'll assume files can be deleted unless explicitly marked as submitted
  const isSubmitted =
    (file as UploadedFileWithSubmissionStatus).submittedToSurePrep === true;
  if (isSubmitted) {
    return {
      canDelete: false,
      reason: "Cannot delete files that have been submitted to SurePrep",
    };
  }

  return { canDelete: true };
};

/**
 * Delete a file from a job
 */
export const deleteJobFile = async (
  firmId: string,
  jobId: string,
  s3Key: string,
): Promise<void> => {
  try {
    // Get auth token from Cognito session
    const session = await fetchAuthSession();
    if (!session.tokens?.idToken) {
      throw new FileDeleteError("Authentication required", 401);
    }

    const token = session.tokens.idToken.toString();

    // Use s3Key as query parameter to avoid URL path encoding issues
    const queryParams = new URLSearchParams({ s3Key });

    const response = await fetch(
      `${appConfig.api.baseUrl}/firms/${firmId}/jobs/${jobId}/files?${queryParams}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new FileDeleteError(
        errorData.message || `Failed to delete file (${response.status})`,
        response.status,
      );
    }

    // Parse success response
    const result = await response.json();
    console.log("File deleted successfully:", result);
  } catch (error) {
    if (error instanceof FileDeleteError) {
      throw error;
    }

    // Handle network errors or other unexpected errors
    throw new FileDeleteError(
      error instanceof Error
        ? error.message
        : "Unknown error occurred while deleting file",
    );
  }
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number | undefined): string => {
  if (bytes === undefined || bytes === null) return "Unknown size";
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Get file extension from filename
 */
export const getFileExtension = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf(".");
  return lastDotIndex > 0
    ? fileName.substring(lastDotIndex + 1).toLowerCase()
    : "";
};

/**
 * Get MIME type from file extension
 */
export const getMimeTypeFromExtension = (fileName: string): string | null => {
  const ext = getFileExtension(fileName).toLowerCase();

  const mimeTypes: Record<string, string> = {
    // Images
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    webp: "image/webp",
    bmp: "image/bmp",
    svg: "image/svg+xml",

    // Documents
    pdf: "application/pdf",

    // Add more as needed
  };

  return mimeTypes[ext] || null;
};

/**
 * Check if file type can be previewed
 */
export const canPreviewFile = (
  contentType?: string,
  fileName?: string,
): boolean => {
  // First check explicit content type
  if (contentType && contentType !== "application/octet-stream") {
    return (
      contentType.startsWith("image/") || contentType === "application/pdf"
    );
  }

  // If content type is generic or missing, check file extension
  if (fileName) {
    const mimeFromExt = getMimeTypeFromExtension(fileName);
    if (mimeFromExt) {
      return (
        mimeFromExt.startsWith("image/") || mimeFromExt === "application/pdf"
      );
    }
  }

  return false;
};

// Mapping of MIME types to magic byte signatures (hex)
const mimeSignatures: { [mime: string]: string[] } = {
  "application/pdf": ["25504446"], // %PDF
  "application/msword": ["D0CF11E0A1B11AE1"], // DOC (OLE Compound File)
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    "504B0304",
  ], // DOCX (ZIP based)
  "application/vnd.ms-excel": ["D0CF11E0A1B11AE1"], // XLS (OLE Compound File)
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    "504B0304",
  ], // XLSX (ZIP based)
  "text/csv": [], // CSV has no magic bytes
  "image/jpeg": ["FFD8FF"], // JPEG
  "image/png": ["89504E470D0A1A0A"], // PNG
};

// Mapping of MIME types to file extensions
const mimeExtensions: { [mime: string]: string } = {
  "application/pdf": "pdf",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "text/csv": "csv",
  "image/jpeg": "jpg",
  "image/png": "png",
};

/**
 * Convert the first N bytes of a buffer into a hex string.
 *
 * @param buffer - The file data as a Uint8Array
 * @param length - Number of bytes to read from the start
 * @returns Hexadecimal string representation
 */
const bufferToHex = (buffer: Uint8Array, length: number): string =>
  Array.from(buffer.subarray(0, length))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();

/**
 * Get the default file extension for a MIME type.
 *
 * @param mime - The MIME type (e.g., "application/pdf")
 * @returns A single canonical extension, or empty string if unknown
 */
export const getExtensionForMime = (mime: string): string =>
  mimeExtensions[mime] || "";

/**
 * Reads the first N bytes of a File/Blob as Uint8Array.
 *
 * @param file - The File or Blob
 * @param length - Number of bytes to read
 */
export const readFileHeader = async (
  file: Blob,
  length = 16,
): Promise<Uint8Array> => {
  const buffer = await file.slice(0, length).arrayBuffer();
  return new Uint8Array(buffer);
};

/**
 * Confirm a file's magic bytes against known MIME signatures.
 */
export const confirmMimeWithMagicBytes = async (
  file: Blob,
  mime: string,
): Promise<{
  ok: boolean;
  expectedMagicBytes?: string[];
  actualMagicBytes: string;
}> => {
  const signatures = mimeSignatures[mime];

  // If no known signatures → skip validation
  if (!signatures || signatures.length === 0) {
    return {
      ok: true,
      expectedMagicBytes: undefined,
      actualMagicBytes: "",
    };
  }

  const header = await readFileHeader(file, 16);
  const actualMagicBytes = bufferToHex(header, 16);

  const ok = signatures.some((sig) =>
    actualMagicBytes.startsWith(sig.toUpperCase()),
  );

  return {
    ok,
    expectedMagicBytes: signatures,
    actualMagicBytes,
  };
};
